import { cn } from "@design-system/utils";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { TabsScrollButton } from "./tabs-scroll-button";
import {
  tabsItemVariants,
  tabsScrollerVariants,
  tabsWrapperVariants,
} from "./tabs-variants";
import {
  getScrollBehavior,
  scrollActiveTabIntoView,
  SCROLL_STEP_RATIO,
  useTabsOverflow,
} from "./use-tabs-overflow";

function Tabs({
  className,
  activationMode = "manual",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex w-full min-w-0 flex-col gap-2", className)}
      activationMode={activationMode}
      {...props}
    />
  );
}

function TabsList({
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const { canScrollLeft, canScrollRight, hasOverflow } = useTabsOverflow(
    wrapperRef,
    scrollerRef
  );

  // Keep the keyboard-focused tab visible. In manual activation mode arrow
  // keys move focus without flipping data-state, so the trigger's
  // MutationObserver never fires for those transitions.
  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onFocusIn = (e: FocusEvent) => {
      const item = (e.target as Element | null)?.closest<HTMLElement>(
        '[data-slot="tabs-trigger"]'
      );
      if (item) scrollActiveTabIntoView(item, '[data-slot="tabs-list"]');
    };
    scroller.addEventListener("focusin", onFocusIn);
    return () => scroller.removeEventListener("focusin", onFocusIn);
  }, []);

  const handleScrollBy = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: el.clientWidth * SCROLL_STEP_RATIO * direction,
      behavior: getScrollBehavior(),
    });
  };

  return (
    <div
      ref={wrapperRef}
      data-slot="tabs-list-wrapper"
      className={tabsWrapperVariants({ className })}
    >
      {/*
        Always render the scroll buttons so the wrapper's children structure
        stays stable when `hasOverflow` flips. Otherwise React reconciles by
        index and remounts `TabsPrimitive.List` (and every trigger inside),
        which re-runs each trigger's `setRef` initial `scrollIfActive` call
        and resets the scroll position on keyboard navigation.
      */}
      <TabsScrollButton
        direction="left"
        disabled={!hasOverflow || !canScrollLeft}
        onClick={() => handleScrollBy(-1)}
        dataSlot="tabs-scroll-button-left"
      />
      {/*
        `{...props}` stays on the Radix List so Radix-specific props (e.g.
        `loop`) and accessibility attributes reach the actual tablist element.
        Only `className` is hoisted to the outer wrapper so consumer layout
        classes (spacing, width) override the outermost rendered element.
      */}
      <TabsPrimitive.List
        ref={scrollerRef}
        data-slot="tabs-list"
        className={tabsScrollerVariants()}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
      <TabsScrollButton
        direction="right"
        disabled={!hasOverflow || !canScrollRight}
        onClick={() => handleScrollBy(1)}
        dataSlot="tabs-scroll-button-right"
      />
    </div>
  );
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(function TabsTrigger({ className, ...props }, forwardedRef) {
  const observerRef = React.useRef<MutationObserver | null>(null);
  const nodeRef = React.useRef<HTMLButtonElement | null>(null);

  // One-shot scroll for the initially active tab (e.g. a non-first
  // `defaultValue`). Keeping this in a `useEffect` instead of the ref
  // callback avoids triggering a scroll whenever React happens to re-run
  // the ref callback during an unrelated re-render.
  React.useEffect(() => {
    const node = nodeRef.current;
    if (!node || node.dataset.state !== "active") return;
    scrollActiveTabIntoView(node, '[data-slot="tabs-list"]');
  }, []);

  const setRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      nodeRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;

      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) return;

      // Radix toggles data-state on the DOM node via context updates; the
      // outer wrapper doesn't re-render, so we observe the attribute
      // directly to catch programmatic value changes.
      const observer = new MutationObserver(() => {
        if (node.dataset.state !== "active") return;
        scrollActiveTabIntoView(node, '[data-slot="tabs-list"]');
      });
      observer.observe(node, {
        attributes: true,
        attributeFilter: ["data-state"],
      });
      observerRef.current = observer;
    },
    [forwardedRef]
  );

  return (
    <TabsPrimitive.Trigger
      ref={setRef}
      data-slot="tabs-trigger"
      className={tabsItemVariants({ activeSelector: "state", className })}
      tabIndex={0}
      {...props}
    />
  );
});

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
